// Store unifié de la plateforme (frontend uniquement).
//
// Immeubles, biens et locataires sont désormais stockés à un seul endroit,
// partagé par les trois espaces (Propriétaire, Agence, Super Admin). Chaque
// enregistrement porte un `scope` indiquant l'espace d'origine, ce qui évite
// toute duplication de données entre les espaces.
//
// Le jour où la base de données est branchée, ce store devient une table
// `immeubles` / `biens` / `locataires` avec une colonne `scope` et `owner_id`.

export type PlatformScope = "proprietaire" | "agence";

export const PLATFORM_SCOPE_LABELS: Record<PlatformScope, string> = {
  proprietaire: "Espace Propriétaire",
  agence: "Espace Agence",
};

export interface PlatformImmeuble {
  id: string;
  scope: PlatformScope;
  /** Identifiant du propriétaire géré (espace Agence). Vide pour un propriétaire autonome. */
  proprietaireId: string;
  nom: string;
  description: string;
  photo: string;
  /** Identifiants territoriaux (FK Supabase : regions/provinces/communes/secteurs). */
  regionId: string;
  provinceId: string;
  communeId: string;
  secteurId: string;
  /** Libellés territoriaux résolus pour l'affichage (non utilisés comme FK). */
  region: string;
  province: string;
  commune: string;
  secteur: string;
  adresse: string;
  niveaux: number;
  infosComplementaires: string;
  createdAt: string;
}

export type PlatformImmeubleInput = Omit<PlatformImmeuble, "id" | "createdAt">;

export interface PlatformBien {
  id: string;
  scope: PlatformScope;
  proprietaireId: string;
  immeubleId: string;
  type: string;
  reference: string;
  etage: string;
  numero: string;
  surface: number;
  loyerMensuel: number;
  statut: string;
  description: string;
  infosComplementaires: string;
  createdAt: string;
}

export type PlatformBienInput = Omit<PlatformBien, "id" | "createdAt">;

export interface PlatformLocataire {
  id: string;
  scope: PlatformScope;
  prenom: string;
  nom: string;
  telephone: string;
  /** Numéro WhatsApp (facultatif) — prépare les futures relances WhatsApp. */
  whatsapp: string;
  email: string;
  adresse: string;
  profession: string;
  typePiece: string;
  numeroPiece: string;
  contactUrgence: string;
  /**
   * Rattachement interne (FK Supabase `proprietaire_id`).
   * Préparé pour le prochain bloc : la valeur dépendra du CRUD Supabase
   * (propriétaires / agences) et ne doit pas être inventée en attendant.
   */
  proprietaireId?: string;
  /**
   * Rattachement interne (FK Supabase `agence_id`).
   * Préparé pour le prochain bloc : la valeur dépendra du CRUD Supabase
   * (propriétaires / agences) et ne doit pas être inventée en attendant.
   */
  agenceId?: string;
  notes: string;
  createdAt: string;
}

export type PlatformLocataireInput = Omit<PlatformLocataire, "id" | "createdAt">;

export function platformLocataireNomComplet(locataire: PlatformLocataire): string {
  return [locataire.prenom, locataire.nom].filter(Boolean).join(" ").trim() || "Locataire sans nom";
}

export function platformLocataireInitiales(locataire: PlatformLocataire): string {
  const prenom = locataire.prenom.trim().charAt(0);
  const nom = locataire.nom.trim().charAt(0);
  return `${prenom}${nom}`.toUpperCase() || "L";
}

/* ------------------------------------------------------------------ */
/* Contrat                                                             */
/*                                                                     */
/* Le contrat relie un locataire à un bien (donc à un immeuble et à     */
/* son propriétaire). Comme les immeubles/biens/locataires, il vit dans */
/* le store unifié et porte un `scope` : un contrat créé dans l'espace  */
/* Propriétaire ou Agence est le même objet vu par le Super Admin.      */
/* ------------------------------------------------------------------ */

export type PlatformContratStatut = "brouillon" | "actif" | "expire" | "resilie" | "suspendu";

export interface PlatformContrat {
  id: string;
  scope: PlatformScope;
  /** Propriétaire géré (espace Agence). Vide pour un propriétaire autonome. */
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  dateDebut: string;
  dateFin: string;
  loyerMensuel: number;
  caution: number;
  avance: number;
  jourEcheance: number;
  conditionsParticulieres: string;
  document: string;
  statut: PlatformContratStatut;
  createdAt: string;
}

export type PlatformContratInput = Omit<PlatformContrat, "id" | "createdAt">;

export const PLATFORM_CONTRAT_STATUT_LABELS: Record<PlatformContratStatut, string> = {
  brouillon: "Brouillon",
  actif: "Actif",
  expire: "Expiré",
  resilie: "Résilié",
  suspendu: "Suspendu",
};

export const PLATFORM_CONTRAT_STATUT_TONES: Record<
  PlatformContratStatut,
  "primary" | "accent" | "secondary" | "neutral" | "danger"
> = {
  brouillon: "neutral",
  actif: "secondary",
  expire: "accent",
  resilie: "danger",
  suspendu: "accent",
};

export const PLATFORM_CONTRAT_STATUT_OPTIONS = (
  Object.keys(PLATFORM_CONTRAT_STATUT_LABELS) as PlatformContratStatut[]
).map((value) => ({ value, label: PLATFORM_CONTRAT_STATUT_LABELS[value] }));

export function platformContratReference(id: string): string {
  return `CTR-${id.slice(-6).toUpperCase()}`;
}

/** Le locataire d'un bien est déterminé par son contrat actif, s'il existe. */
export function isPlatformContratActif(contrat: PlatformContrat): boolean {
  return contrat.statut === "actif";
}